import { Link } from "react-router-dom";
import { ArrowRight, BookOpen } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import PageHero from "@/components/PageHero";

/* Generic content page used for menu destinations that share a single
   layout shell (How It Works, General Rules, Affiliates landing, etc.).
   Each route passes its own copy via the props below.

   SEO additions:
   - Breadcrumb visible row (machine breadcrumb is handled by SEOHead)
   - Internal-link "Related" block at the bottom for crawl depth + UX
   - Larger H2 hierarchy with anchor IDs for jump links */
export default function GenericInfoPage({
  pill, title, accent, intro, sections,
  ctaPrimary, ctaPrimaryHref, ctaSecondary, ctaSecondaryHref, testId,
  breadcrumbs = [],
  related = [],
  relatedHeading,
}) {
  return (
    <div className="min-h-screen bg-[color:var(--bg)]" data-testid={testId}>
      <Navbar />
      <PageHero
        breadcrumb={breadcrumbs}
        pill={pill ? { text: pill } : undefined}
        title={title}
        accent={accent}
        subline={intro}
        primary={ctaPrimary ? { label: ctaPrimary, to: ctaPrimaryHref } : undefined}
        secondary={ctaSecondary ? { label: ctaSecondary, to: ctaSecondaryHref } : undefined}
      />
      <main className="pb-20">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="grid gap-6">
            {sections.map((s) => {
              const anchor = (s.h || "").toLowerCase().replace(/[^a-z0-9]+/g, "-");
              return (
                <section
                  key={s.h}
                  id={anchor}
                  className="card-beam p-6 lg:p-7 scroll-mt-28"
                  style={{ border: "1px solid color-mix(in srgb, var(--brand) 14%, transparent)" }}
                >
                  <h2 className="font-display text-xl lg:text-2xl font-medium text-[color:var(--text-1)] mb-2 flex items-center gap-2">
                    {s.Icon && <s.Icon size={18} className="text-brand" />}
                    {s.h}
                  </h2>
                  <p className="text-sm lg:text-base text-[color:var(--text-2)] font-light leading-relaxed">{s.body}</p>
                  {s.bullets && (
                    <ul className="mt-4 grid gap-2">
                      {s.bullets.map((b) => (
                        <li key={b} className="text-sm text-[color:var(--text-2)] flex items-start gap-2">
                          <ArrowRight size={14} className="text-brand mt-0.5 shrink-0" />
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
              );
            })}
          </div>

          {(ctaPrimary || ctaSecondary) && (
            <div className="mt-12 flex flex-col sm:flex-row gap-3 justify-center">
              {ctaPrimary && (
                <Link
                  to={ctaPrimaryHref}
                  className="btn-solid-brand text-sm inline-flex items-center justify-center gap-2"
                  style={{ height: "48px", width: "220px", padding: "0 1.5rem", lineHeight: 1 }}
                >
                  {ctaPrimary} <ArrowRight size={16} />
                </Link>
              )}
              {ctaSecondary && (
                <Link
                  to={ctaSecondaryHref}
                  className="btn-ghost-gradient text-sm inline-flex items-center justify-center gap-2"
                  style={{ height: "48px", width: "220px", padding: "0 1.5rem", lineHeight: 1 }}
                >
                  {ctaSecondary} <BookOpen size={14} />
                </Link>
              )}
            </div>
          )}

          {/* Related links block · boosts internal-link graph for SEO */}
          {related.length > 0 && (
            <div className="mt-16 pt-10 border-t border-[color:var(--line)]/60">
              <div className="text-[12px] uppercase tracking-[0.22em] text-[color:var(--text-3)] font-semibold mb-4">
                {relatedHeading || "Related at OFinancial Markets"}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {related.map((r) => (
                  <Link
                    key={r.to}
                    to={r.to}
                    className="group flex items-start gap-3 p-4 rounded-xl card-beam hover:border-brand/40 transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-[color:var(--text-1)] group-hover:text-brand transition-colors">
                        {r.label}
                      </div>
                      {r.tagline && (
                        <div className="text-[12px] text-[color:var(--text-3)] mt-0.5 font-light">
                          {r.tagline}
                        </div>
                      )}
                    </div>
                    <ArrowRight size={14} className="text-[color:var(--text-3)] group-hover:text-brand mt-1 shrink-0 transition-colors" />
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
